import {
  getApiOverview,
  getMapApiOverview
} from '@/services/diagnostics-service'
import { formatDate, formatTime } from '@smarttransit/common'
import { addAlert } from '@/utilities/helpers'
import { getWorstStatus, statusLabel } from './components/status-mapping'
import SystemHealthBanner from './components/SystemHealthBanner.vue'
import OverviewTab from './tabs/overview.vue'
import BoardingPipelineTab from './tabs/boarding-pipeline.vue'
import PricingLoopTab from './tabs/pricing-loop.vue'
import MapApiWorkersTab from './tabs/map-api-workers.vue'
import ScheduledJobsTab from './tabs/scheduled-jobs.vue'

const POLL_INTERVAL_MS = 15000
const TAB_VALUES = ['overview', 'progressive-transportation', 'pricing', 'map-api', 'scheduled-jobs']
const TAB_ALIASES = {
  boarding: 'progressive-transportation'
}

export default {
  name: 'advanced-realtime-diagnostics',
  components: {
    SystemHealthBanner,
    OverviewTab,
    BoardingPipelineTab,
    PricingLoopTab,
    MapApiWorkersTab,
    ScheduledJobsTab
  },
  props: {
    signedInUser: Object
  },
  data () {
    return {
      activeTab: 'overview',
      autoPollEnabled: true,
      pollHandle: null,
      isPageActive: false,
      apiInProgress: false,
      silentRefresh: false,
      refreshToken: 0,
      lastRefreshedAt: null,
      apiOverview: null,
      mapApiOverview: null
    }
  },
  computed: {
    globalStatuses () {
      const statuses = []
      const apiSystems = this.apiOverview && this.apiOverview.systems ? this.apiOverview.systems : {}
      const mapSystems = this.mapApiOverview && this.mapApiOverview.systems ? this.mapApiOverview.systems : {}

      if (apiSystems.boardingPipeline && apiSystems.boardingPipeline.status) {
        statuses.push(apiSystems.boardingPipeline.status)
      }
      if (apiSystems.pricingLoop && apiSystems.pricingLoop.status) {
        statuses.push(apiSystems.pricingLoop.status)
      }
      if (apiSystems.scheduledJobs && apiSystems.scheduledJobs.status) {
        statuses.push(apiSystems.scheduledJobs.status)
      }
      if (apiSystems.messageQueue && apiSystems.messageQueue.status) {
        statuses.push(apiSystems.messageQueue.status)
      }
      if (mapSystems.geoBackup && mapSystems.geoBackup.status) {
        statuses.push(mapSystems.geoBackup.status)
      }
      if (mapSystems.requestQueues && mapSystems.requestQueues.status) {
        statuses.push(mapSystems.requestQueues.status)
      }

      return statuses
    },
    globalStatus () {
      return getWorstStatus(this.globalStatuses)
    },
    globalTitle () {
      return 'Realtime Operations Diagnostics: ' + statusLabel(this.globalStatus)
    },
    globalSubtitle () {
      if (!this.globalStatuses.length) {
        return 'No diagnostics data loaded yet.'
      }

      let critical = 0
      let warning = 0
      this.globalStatuses.forEach(function (status) {
        if (status === 'critical') {
          critical += 1
        } else if (status === 'warning' || status === 'stale') {
          warning += 1
        }
      })

      if (critical > 0) {
        return critical + ' system(s) critical' + (warning > 0 ? (', ' + warning + ' warning') : '')
      }
      if (warning > 0) {
        return warning + ' system(s) need attention'
      }
      return 'All monitored systems are operational.'
    },
    lastUpdatedLabel () {
      if (!this.lastRefreshedAt) {
        return ''
      }

      return 'Last refreshed: ' + formatDate(this.lastRefreshedAt) + ', ' + formatTime(this.lastRefreshedAt)
    }
  },
  mounted () {
    this.applyRouteTab()
    this.isPageActive = true
    this.refreshAll({ silent: false })
  },
  beforeRouteUpdate (to, from, next) {
    this.applyRouteTab(to)
    next()
  },
  beforeRouteLeave (to, from, next) {
    this.cleanupPolling()
    next()
  },
  deactivated () {
    this.cleanupPolling()
  },
  activated () {
    this.applyRouteTab()
    this.isPageActive = true
    this.refreshAll({ silent: false })
  },
  beforeDestroy () {
    this.cleanupPolling()
  },
  watch: {
    autoPollEnabled (value) {
      if (value) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
    activeTab (value) {
      this.syncTabToRoute(value)
    },
    '$route.hash' () {
      this.applyRouteTab()
    }
  },
  methods: {
    async refreshAll (options) {
      if (!this.isPageActive) {
        return
      }

      const silent = !!(options && options.silent)
      this.silentRefresh = silent

      if (!silent) {
        this.apiInProgress = true
      }

      try {
        const responses = await Promise.all([
          getApiOverview(),
          getMapApiOverview()
        ])

        if (!this.isPageActive) {
          return
        }

        this.apiOverview = responses[0]
        this.mapApiOverview = responses[1]
        this.refreshToken += 1
        this.lastRefreshedAt = new Date()
      } catch (err) {
        if (this.isPageActive) {
          addAlert({
            type: 'error',
            message: err && err.message ? err.message : 'Failed to load diagnostics overview'
          })
        }
      } finally {
        if (this.isPageActive && !silent) {
          this.apiInProgress = false
        }
      }

      if (this.isPageActive && this.autoPollEnabled) {
        this.startPolling()
      }
    },
    startPolling () {
      if (!this.isPageActive) {
        return
      }

      this.stopPolling()
      this.pollHandle = setTimeout(async () => {
        if (!this.isPageActive || !this.autoPollEnabled) {
          return
        }
        await this.refreshAll({ silent: true })
      }, POLL_INTERVAL_MS)
    },
    stopPolling () {
      clearTimeout(this.pollHandle)
      this.pollHandle = null
    },
    cleanupPolling () {
      this.isPageActive = false
      this.stopPolling()
      this.apiInProgress = false
    },
    getRouteTab (route) {
      const routeHash = route && route.hash ? route.hash.replace(/^#/, '') : ''
      if (TAB_ALIASES[routeHash]) {
        return TAB_ALIASES[routeHash]
      }
      return TAB_VALUES.indexOf(routeHash) > -1 ? routeHash : 'overview'
    },
    applyRouteTab (route) {
      const routeTab = this.getRouteTab(route || this.$route)
      if (this.activeTab !== routeTab) {
        this.activeTab = routeTab
      }
    },
    syncTabToRoute (tabValue) {
      if (TAB_VALUES.indexOf(tabValue) === -1) {
        return
      }

      const nextHash = '#' + tabValue
      if (this.$route && this.$route.hash === nextHash) {
        return
      }

      this.$router.replace({
        path: this.$route.path,
        query: this.$route.query,
        hash: nextHash
      }).catch(_ => {})
    },
    onNavigateTab (tabValue) {
      this.activeTab = tabValue
    }
  }
}
